/*
 * Comparing what runs in this tab against what the server currently
 * serves.
 *
 * `tools/remote` writes a version.txt into every release:
 *
 *   commit: 0f2ed02f1c…        (the full sha, with a -dirty suffix
 *   branch: mihoe/…             when built from a dirty worktree)
 *   environment: int           (which environment it was deployed to)
 *   release: release-20260725-082724
 *   built: 2026-07-25T08:27:24+02:00
 *
 * The running app only knows the *short* sha, injected by the webpack
 * DefinePlugin — hence the prefix comparison rather than equality.
 *
 * The file is written at deploy time only. In the dev server and in a
 * plain `npm run build` it does not exist, and the injected commit is
 * the marker `dev`; both cases count as "nothing to report" rather
 * than as an error.
 */

const RUNNING_COMMIT =
  typeof __GIT_COMMIT__ === 'undefined' ? 'dev' : __GIT_COMMIT__;

const RUNNING_BUILD_TIME =
  typeof __BUILD_TIME__ === 'undefined' ? '' : __BUILD_TIME__;

/* markers webpack injects where there is no real commit */
const NOT_A_RELEASE = ['dev', 'unknown', ''];

/*
 * What identifies *this* build — the commit alone does not.
 * `tools/remote` deploys the working tree, so two releases can be
 * built from the same sha (an unchanged HEAD, or the same HEAD with
 * uncommitted work). Everything that compares builds therefore
 * compares the stamp of the build, not of the commit it came from.
 */
const RUNNING_BUILD = `${RUNNING_COMMIT}@${RUNNING_BUILD_TIME}`;

/* the build markers travel with a build time appended, so the marker
   is the part in front of it */
export function isRelease(build = RUNNING_BUILD) {
  return !NOT_A_RELEASE.includes(String(build).split('@')[0]);
}

export function parseVersionFile(text) {
  if (typeof text !== 'string') return null;

  const fields = {};
  text.split('\n').forEach((line) => {
    const separator = line.indexOf(':');
    if (separator < 0) return;
    fields[line.slice(0, separator).trim()] = line.slice(separator + 1).trim();
  });

  return fields.commit ? fields : null;
}

/* deliberately conservative: anything we cannot interpret means "no
   update", so an unreachable server or a stray HTML page (which the
   SPA rewrite hands out for unknown paths) never nags the user */
export function isOutdated(deployed, running = RUNNING_COMMIT) {
  if (!deployed?.commit) return false;
  if (NOT_A_RELEASE.includes(running)) return false;
  return !deployed.commit.startsWith(running);
}

/*
 * The other half of the same question, for the case the commit cannot
 * answer: a redeploy of the same sha serves different code under the
 * same commit, and `isOutdated` calls that current.
 *
 * The release id in version.txt is minted per deployment and does tell
 * them apart. What it cannot tell by itself is which release *this*
 * tab was loaded from — hence `loadedRelease`, the release the first
 * answer of this page load reported. That first answer therefore never
 * reports an update; every later one compares against it.
 */
export function movedOn(deployed, loadedRelease, running = RUNNING_COMMIT) {
  if (!deployed?.release || !loadedRelease) return false;
  if (NOT_A_RELEASE.includes(running)) return false;
  return deployed.release !== loadedRelease;
}

export async function fetchDeployedVersion(signal) {
  try {
    const response = await fetch('/version.txt', { cache: 'no-store', signal });
    if (!response.ok) return null;

    /* the SPA rewrite answers unknown paths with index.html — do not
       try to read a version out of that */
    const type = response.headers.get('content-type') || '';
    if (type.includes('html')) return null;

    return parseVersionFile(await response.text());
  } catch {
    /* offline, aborted, blocked — all simply "we do not know" */
    return null;
  }
}

export { RUNNING_COMMIT, RUNNING_BUILD };
